const AFFILIATE_STORAGE_KEY = "aid";
const MARKETING_STORAGE_KEY = "ms";
const AFFILIATE_COOKIE_KEY = "bl_aid";
const MARKETING_COOKIE_KEY = "bl_ms";
const AFFILIATE_COOKIE_DAYS = 90;

const AFFILIATE_PARAM_NAMES = ["aid", "ref", "refid", "refId", "affiliate"];

export const normalizeTrackingValue = (value) => {
  if (value == null) {
    return null;
  }

  const trimmed = String(value).trim();
  if (!trimmed || trimmed === "null" || trimmed === "undefined") {
    return null;
  }

  return trimmed;
};

const setCookie = (name, value, days = AFFILIATE_COOKIE_DAYS) => {
  if (typeof document === "undefined") {
    return;
  }

  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(
    value
  )};expires=${expires};path=/;SameSite=Lax`;
};

const getCookie = (name) => {
  if (typeof document === "undefined") {
    return null;
  }

  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

export const persistAffiliateKey = (value) => {
  const normalized = normalizeTrackingValue(value);
  if (!normalized) {
    return null;
  }

  localStorage.setItem(AFFILIATE_STORAGE_KEY, normalized);
  setCookie(AFFILIATE_COOKIE_KEY, normalized);
  return normalized;
};

export const persistMarketingKey = (value) => {
  const normalized = normalizeTrackingValue(value);
  if (!normalized) {
    return null;
  }

  localStorage.setItem(MARKETING_STORAGE_KEY, normalized);
  setCookie(MARKETING_COOKIE_KEY, normalized);
  return normalized;
};

export const getStoredAffiliateKey = () =>
  normalizeTrackingValue(localStorage.getItem(AFFILIATE_STORAGE_KEY)) ||
  normalizeTrackingValue(getCookie(AFFILIATE_COOKIE_KEY));

export const getStoredMarketingKey = () =>
  normalizeTrackingValue(localStorage.getItem(MARKETING_STORAGE_KEY)) ||
  normalizeTrackingValue(getCookie(MARKETING_COOKIE_KEY));

export const hydrateAffiliateFromCookie = () => {
  const cookieAid = normalizeTrackingValue(getCookie(AFFILIATE_COOKIE_KEY));
  const cookieMs = normalizeTrackingValue(getCookie(MARKETING_COOKIE_KEY));

  if (cookieAid && !localStorage.getItem(AFFILIATE_STORAGE_KEY)) {
    localStorage.setItem(AFFILIATE_STORAGE_KEY, cookieAid);
  }

  if (cookieMs && !localStorage.getItem(MARKETING_STORAGE_KEY)) {
    localStorage.setItem(MARKETING_STORAGE_KEY, cookieMs);
  }
};

const readAffiliateFromParams = (params) => {
  for (const name of AFFILIATE_PARAM_NAMES) {
    const value = normalizeTrackingValue(params.get(name));
    if (value) {
      return value;
    }
  }

  return null;
};

export const captureAffiliateFromSearch = (search = "") => {
  if (typeof window === "undefined") {
    return {
      aid: null,
      ms: null,
    };
  }

  const rawSearch = search || window.location.search || "";
  const params = new URLSearchParams(
    rawSearch.startsWith("?") ? rawSearch : `?${rawSearch}`
  );

  const aidFromUrl = readAffiliateFromParams(params);
  const msFromUrl = normalizeTrackingValue(params.get("ms"));

  if (aidFromUrl) {
    persistAffiliateKey(aidFromUrl);
  }

  if (msFromUrl) {
    persistMarketingKey(msFromUrl);
  }

  return {
    aid: aidFromUrl || getStoredAffiliateKey(),
    ms: msFromUrl || getStoredMarketingKey(),
  };
};

export const buildRegisterPath = (planId) => {
  const params = new URLSearchParams();

  if (planId != null && planId !== "") {
    params.set("planId", String(planId));
  }

  const aid = getStoredAffiliateKey();
  const ms = getStoredMarketingKey();

  if (aid) {
    params.set("aid", aid);
  }

  if (ms) {
    params.set("ms", ms);
  }

  const query = params.toString();
  return query ? `/register?${query}` : "/register";
};

export const preserveTrackingKeysDuringSessionClear = () => {
  const aid = getStoredAffiliateKey();
  const ms = getStoredMarketingKey();

  return { aid, ms };
};

export const restoreTrackingKeysAfterSessionClear = ({ aid, ms }) => {
  if (aid) {
    persistAffiliateKey(aid);
  }

  if (ms) {
    persistMarketingKey(ms);
  }
};

export const initAffiliateTracking = () => {
  hydrateAffiliateFromCookie();
  captureAffiliateFromSearch(window.location.search);
};

if (typeof window !== "undefined") {
  initAffiliateTracking();
}
